import React, { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";

const humanizeKey = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const isDateOnly = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const formatValue = (key, value) => {
  if (value === null || typeof value === "undefined" || value === "") return "-";

  if (key === "step") {
    const step = Number(value);
    if (step === 3) return "Paid (Step 3)";
    if (step === 2) return "Incomplete (Step 2)";
    if (Number.isFinite(step)) return `Step ${step}`;
    return String(value);
  }

  if (key === "charge" || key === "Charge") {
    const num = Number(value);
    return Number.isFinite(num) ? `$${num.toFixed(2)}` : String(value);
  }

  if (key === "cleaningHoursApprox") {
    const num = Number(value);
    return Number.isFinite(num) ? `${num} hrs` : String(value);
  }

  if (key.endsWith("Time") && typeof value === "string" && value.length >= 5 && /^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }

  if (
    key.toLowerCase().includes("date") ||
    key.toLowerCase().includes("created") ||
    key.toLowerCase().includes("scheduled")
  ) {
    if (value instanceof Date) return value.toLocaleString();
    if (typeof value === "string" && isDateOnly(value)) return value;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "-";

  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : "-";
  if (typeof value === "object") return JSON.stringify(value, null, 2);

  return String(value);
};

const buildEntries = (booking) => {
  if (!booking) return [];

  const keys = Object.keys(booking);
  const hide = new Set();
  if (keys.includes("charge")) hide.add("Charge");
  if (keys.includes("extraHours")) hide.add("ExtraHours");

  const preferredOrder = [
    "serviceId",
    "step",
    "creationDate",
    "firstName",
    "lastName",
    "email",
    "phone",
    "serviceType",
    "cleanType",
    "frequency",
    "bedrooms",
    "bathrooms",
    "hours",
    "minutes",
    "preferredDate",
    "preferredTime",
    "streetAddress",
    "suburb",
    "postcode",
    "getInOption",
    "parkOption",
    "specialInstructions",
    "carpetOptions",
    "extraHours",
    "charge",
    "cleaningHoursApprox",
    "assignmentId",
    "employeeId",
    "scheduledAt",
    "jobStatus",
    "employeeName",
    "employeeEmail",
    "employeeContact",
  ];

  const used = new Set();
  const result = [];

  for (const key of preferredOrder) {
    if (!Object.prototype.hasOwnProperty.call(booking, key)) continue;
    if (hide.has(key)) continue;
    used.add(key);
    result.push({ key, label: humanizeKey(key), value: formatValue(key, booking[key]) });
  }

  const remaining = keys
    .filter((key) => !used.has(key) && !hide.has(key))
    .sort((a, b) => a.localeCompare(b));

  for (const key of remaining) {
    result.push({ key, label: humanizeKey(key), value: formatValue(key, booking[key]) });
  }

  return result;
};

export default function BookingDetailsModal({ open, booking, loading, error, onClose }) {
  const closeRef = useRef(null);

  const entries = useMemo(() => buildEntries(booking), [booking]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (closeRef.current) closeRef.current.focus();
  }, [open]);

  if (!open) return null;

  const title = booking?.serviceId ? `Booking #${booking.serviceId}` : "Booking details";

  return (
    <div
      className="rs-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="rs-card rs-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="rs-modal__header">
          <div>
            <div className="rs-modal__title">{title}</div>
            {loading ? <div className="rs-muted">Loading…</div> : null}
            {!loading && error ? <div className="rs-muted">{error}</div> : null}
          </div>
          <button
            type="button"
            className="rs-icon-btn"
            onClick={() => onClose?.()}
            aria-label="Close"
            title="Close"
            ref={closeRef}
          >
            <X size={18} />
          </button>
        </div>

        <div className="rs-modal__body">
          {loading ? (
            <div className="rs-muted">Loading booking details…</div>
          ) : booking ? (
            <div className="rs-kv">
              {entries.map((entry) => (
                <div className="rs-kv__row" key={entry.key}>
                  <div className="rs-kv__k">{entry.label}</div>
                  <div className="rs-kv__v rs-kv__v--modal">{entry.value}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rs-muted">No booking data.</div>
          )}
        </div>
      </div>
    </div>
  );
}

